import React from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";

import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import {
  CountriesAndLanguages,
  AddressFormData,
  AddressField,
} from "@/networking/addresses.api";
import NativeSelect from "../ui/input/native-select";
import { State } from "@/lib/country-config";
import Checkbox from "../ui/input/checkbox";
import { AddressSuggestion } from "./hooks/useAutocomplete";
import { ErrorAlert } from "@/components/ui/alert";
import DynamicAddressFields from "@/components/shared/dynamic-address-fields";
import { resolveAddressCountryCode } from "@/lib/address-country-selection";

export default function AddressDialog({
  countries,
  control,
  setValue,
  isOpen,
  onSubmit,
  handleClose,
  renderAutocompleteOptions,
  getAutocompleteOptions,
  handleOptionClick,
  formCountry,
  stateOptions,
  addressFields,
  handleCountryChange,
  errorMsg,
  isSubmitting,
  extraFooterContent,
}: {
  countries: CountriesAndLanguages["countries"];
  control: Control<AddressFormData>;
  setValue: UseFormSetValue<AddressFormData>;
  isOpen: boolean;
  onSubmit: () => void;
  handleClose: () => void;
  renderAutocompleteOptions: (isFocused: boolean) => React.ReactNode;
  getAutocompleteOptions: (input: string, formCountry: string) => void;
  handleOptionClick: (option: AddressSuggestion, formCountry: string) => void;
  formCountry: string;
  stateOptions: State[];
  addressFields: AddressField[];
  handleCountryChange: (country: string, updateCartCountry?: boolean) => void;
  errorMsg: string | undefined;
  isSubmitting: boolean;
  extraFooterContent?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const defaultAddress = useWatch({ control, name: "default" });
  const formCountryCode = useWatch({ control, name: "country_code" });
  const selectedCountryCode =
    resolveAddressCountryCode(
      { country: formCountry, country_code: formCountryCode },
      countries,
    ) ?? "US";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-sm rounded md:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-md font-medium text-gray-900">
              {t("add_an_address")}
            </DialogTitle>
          </DialogHeader>
          <div className="rounded shadow-xs">
            <NativeSelect
              key={selectedCountryCode || "no-country"}
              control={control}
              name="country_code"
              label={t("country")}
              placeholder={t("select_an_option")}
              options={countries
                ?.map((country) => ({
                  id: country.iso,
                  name: country.name,
                }))
                .sort((a, b) => {
                  if (a.id === selectedCountryCode) return -1;
                  if (b.id === selectedCountryCode) return 1;
                  return a.name.localeCompare(b.name);
                })}
              onSelectChange={(e) => {
                setValue("country", e.name);
                setValue("country_code", e.id.toString());
                setValue("address1", "");
                setValue("address2", "");
                setValue("city", "");
                setValue("state", "");
                setValue("postal_code", "");
                handleCountryChange(e.id.toString(), false);
              }}
              inputStyles="rounded-t"
            />
            <DynamicAddressFields
              addressFields={addressFields}
              stateOptions={stateOptions}
              fieldPathPrefix=""
              control={control}
              formCountry={formCountry}
              countryCode={selectedCountryCode}
              renderAutocompleteOptions={renderAutocompleteOptions}
              getAutocompleteOptions={getAutocompleteOptions}
              handleOptionClick={handleOptionClick}
            />
          </div>
          {errorMsg && <ErrorAlert message={errorMsg} />}
          {extraFooterContent && (
            <div className="pt-1">{extraFooterContent}</div>
          )}
          <DialogFooter className="flex flex-row items-center justify-between">
            <div className="flex-2">
              <Checkbox
                label={t("set_as_default_address")}
                id="default"
                name="default"
                control={control}
                classNames="text-sm"
                onChange={() => {
                  setValue("default", !defaultAddress);
                }}
              />
            </div>
            <div className="flex-1 text-right">
              <DialogClose asChild>
                <Button
                  type="button"
                  onClick={onSubmit}
                  className="bg-brand-primary text-brand-primary-text"
                >
                  {isSubmitting && (
                    <div className="mr-3 h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
                  )}
                  {isSubmitting ? t("saving") : t("save_address")}
                </Button>
              </DialogClose>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
